/**
 * useChattrSocket.js — WebSocket client hook for Chattr
 * Place at: src/hooks/useChattrSocket.js
 *
 * Manages the single WebSocket connection to the Fly.io relay server.
 * All Chattr components (Matchmaker, ChatRoom) talk through this hook.
 *
 * FEATURES:
 *   - Auto-reconnect with exponential backoff (1s → 2s → 4s → max 30s)
 *   - Sends Firebase Auth JWT so server can verify user identity
 *   - Heartbeat pong response (keeps connection alive through proxies)
 *   - Cleans up on unmount (sends leave_room or cancel_queue automatically)
 *   - All server message types handled centrally
 *   - broadenQueue: atomically cancel + re-queue with a new interest without
 *     any intermediate state transitions (no "idle" flash)
 *
 * USAGE:
 *   const {
 *     status,           // 'connecting' | 'connected' | 'disconnected'
 *     joinQueue,        // (nickname, interest) → start matchmaking
 *     cancelQueue,      // () → leave queue
 *     broadenQueue,     // (nickname, newInterest) → cancel + re-queue atomically
 *     sendMessage,      // (text) → send chat message
 *     sendTyping,       // (isTyping) → typing indicator
 *     leaveRoom,        // () → leave current chat
 *     matchData,        // { roomId, partnerNickname, interest } when matched
 *     messages,         // Message[] — current conversation
 *     partnerTyping,    // bool
 *     queueStatus,      // 'idle' | 'queued' | 'matched' | 'timeout'
 *     error,            // string | null
 *   } = useChattrSocket(currentUser)
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { auth } from "../firebase";

//const WS_URL = import.meta.env.VITE_CHATTR_WS_URL || "wss://healhub-chattr.fly.dev";
const WS_URL = process.env.REACT_APP_CHATTR_WS_URL || "ws://localhost:8080";
export function useChattrSocket(currentUser) {
  const [status,        setStatus]        = useState("disconnected");
  const [queueStatus,   setQueueStatus]   = useState("idle");
  const [matchData,     setMatchData]     = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const [error,         setError]         = useState(null);

  const socketRef       = useRef(null);
  const retryCountRef   = useRef(0);
  const retryTimerRef   = useRef(null);
  const isMountedRef    = useRef(true);
  const roomIdRef       = useRef(null);
  const queueStateRef   = useRef({ inQueue: false, interest: null });
  const typingTimerRef  = useRef(null);

  /**
   * ── Broadening ref ───────────────────────────────────────────────────────
   *
   * When broadenQueue() fires, it sends cancel_queue + join_queue in the
   * same synchronous tick over the same WebSocket. The server processes
   * them in order (WebSocket guarantees message ordering).
   *
   * Problem: the server responds with "queue_cancelled" for the cancel,
   * which normally sets queueStatus → "idle" and flashes the user to the
   * home screen. A racing "match_timeout" from the server's own 30s timer
   * could also arrive and kick the user out.
   *
   * Solution: this ref acts as a one-shot flag. While true, the message
   * handler suppresses the next "queue_cancelled" and any "match_timeout"
   * that arrives before the new "queued" confirmation. The flag is consumed
   * (set to false) on the first suppressed event OR when "queued" arrives,
   * whichever comes first. This guarantees:
   *   - queueStatus stays "queued" throughout — no intermediate "idle"
   *   - No view flash in the component
   *   - No race with server-side timeout
   */
  const isBroadeningRef = useRef(false);

  // ── Connect ──────────────────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (!isMountedRef.current) return;
    if (socketRef.current?.readyState === WebSocket.OPEN) return;

    setStatus("connecting");
    setError(null);

    const ws = new WebSocket(WS_URL);
    socketRef.current = ws;

    ws.onopen = () => {
      if (!isMountedRef.current) { ws.close(); return; }
      setStatus("connected");
      retryCountRef.current = 0;
      console.log("✅ Chattr WebSocket connected");
    };

    ws.onmessage = (event) => {
      if (!isMountedRef.current) return;
      let msg;
      try { msg = JSON.parse(event.data); }
      catch { return; }

      switch (msg.type) {

        case "queued":
          // If we're broadening, the new "queued" confirmation means the
          // re-queue succeeded. Consume the broadening flag.
          if (isBroadeningRef.current) {
            isBroadeningRef.current = false;
          }
          setQueueStatus("queued");
          break;

        case "matched":
          // If a match arrives during broadening, accept it — someone matched
          // us on the old or new interest, either is fine.
          isBroadeningRef.current = false;
          roomIdRef.current = msg.roomId;
          setMatchData({ roomId: msg.roomId, partnerNickname: msg.partnerNickname, interest: msg.interest });
          setQueueStatus("matched");
          setMessages([]);
          break;

        case "message":
          // Use server-provided stable ID for reaction matching
          setMessages(prev => [...prev, {
            id:        msg.id || `${msg.timestamp}-${Math.random()}`,
            text:      msg.text,
            sender:    msg.sender,
            timestamp: msg.timestamp,
            isSelf:    false,
          }]);
          // Clear typing indicator when message arrives
          setPartnerTyping(false);
          break;

        case "typing":
          setPartnerTyping(!!msg.isTyping);
          // Auto-clear typing after 3s in case server misses stop event
          clearTimeout(typingTimerRef.current);
          if (msg.isTyping) {
            typingTimerRef.current = setTimeout(() => setPartnerTyping(false), 3000);
          }
          break;

        case "partner_left":
          setMatchData(prev => prev ? { ...prev, partnerLeft: true, partnerLeftReason: msg.reason } : null);
          roomIdRef.current = null;
          setPartnerTyping(false);
          break;

        case "left_room":
          roomIdRef.current = null;
          setMatchData(null);
          setQueueStatus("idle");
          setMessages([]);
          setPartnerTyping(false);
          break;

        case "queue_cancelled":
          // ── BROADENING GUARD ──
          // If we're in the middle of a broaden cycle, the server is
          // confirming the cancel half. Suppress the idle transition —
          // the join_queue we already sent will produce a new "queued".
          if (isBroadeningRef.current) {
            // Don't consume the flag yet — wait for the "queued" confirmation
            break;
          }
          setQueueStatus("idle");
          queueStateRef.current = { inQueue: false, interest: null };
          break;

        case "match_timeout":
          // ── BROADENING GUARD ──
          // If the server's own timeout fires right as we broaden, suppress
          // it. The re-queue join_queue we already sent will start a fresh
          // server-side timeout.
          if (isBroadeningRef.current) {
            break;
          }
          setQueueStatus("timeout");
          setError("No match found. Try a different topic or try again.");
          queueStateRef.current = { inQueue: false, interest: null };
          break;

        case "server_restart":
          setError("Server is restarting. Reconnecting…");
          break;

        // Server confirms our message was delivered — swap temp ID with stable server ID
        case "message_sent":
          setMessages(prev => prev.map(m =>
            m.id === msg.tempId
              ? { ...m, id: msg.id, timestamp: msg.timestamp }
              : m
          ));
          break;

        // Partner reacted to one of our messages
        case "reaction":
          setMessages(prev => prev.map(m =>
            m.id === msg.messageId
              ? { ...m, theirReaction: msg.emoji }
              : m
          ));
          break;

        case "error":
          setError(msg.message);
          break;

        default:
          break;
      }
    };

    ws.onclose = () => {
      if (!isMountedRef.current) return;
      setStatus("disconnected");
      socketRef.current = null;

      // Exponential backoff reconnect
      const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 30_000);
      retryCountRef.current++;
      console.log(`🔄 Reconnecting in ${delay}ms (attempt ${retryCountRef.current})`);
      retryTimerRef.current = setTimeout(connect, delay);
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
    };
  }, []);

  // ── Init on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    if (currentUser) connect();

    return () => {
      isMountedRef.current = false;
      clearTimeout(retryTimerRef.current);
      clearTimeout(typingTimerRef.current);

      // Graceful cleanup on unmount
      const ws = socketRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        if (roomIdRef.current) {
          ws.send(JSON.stringify({ type: "leave_room", roomId: roomIdRef.current }));
        } else if (queueStateRef.current.inQueue) {
          ws.send(JSON.stringify({ type: "cancel_queue" }));
        }
        ws.close();
      }
    };
  }, [currentUser, connect]);

  // ── Send helper ──────────────────────────────────────────────────────────
  const sendRaw = useCallback((data) => {
    const ws = socketRef.current;
    if (ws?.readyState !== WebSocket.OPEN) {
      setError("Not connected. Reconnecting…");
      return false;
    }
    ws.send(JSON.stringify(data));
    return true;
  }, []);

  // ── Public actions ───────────────────────────────────────────────────────

  const joinQueue = useCallback((nickname, interest) => {
    if (!currentUser?.uid) { setError("Please log in first."); return; }
    setError(null);
    setQueueStatus("idle");
    queueStateRef.current = { inQueue: true, interest };
    sendRaw({
      type:     "join_queue",
      nickname: nickname.trim(),
      interest,
      userId:   currentUser.uid,
    });
  }, [currentUser, sendRaw]);

  const cancelQueue = useCallback(() => {
    queueStateRef.current = { inQueue: false, interest: null };
    sendRaw({ type: "cancel_queue" });
    setQueueStatus("idle");
  }, [sendRaw]);

  /**
   * broadenQueue — atomically cancel the current queue entry and re-join
   * with a new interest, without any intermediate "idle" state transition.
   *
   * Both messages are sent in the same synchronous tick over the same
   * WebSocket connection. WebSocket guarantees ordering, so the server
   * will always process cancel_queue before join_queue.
   *
   * The isBroadeningRef flag tells the message handler to suppress the
   * "queue_cancelled" and any racing "match_timeout" from the server,
   * preventing the component from flashing back to the home screen.
   *
   * @param {string} nickname  — user's current nickname
   * @param {string} newInterest — the broadened interest (e.g. "Any")
   */
  const broadenQueue = useCallback((nickname, newInterest) => {
    if (!currentUser?.uid) return;

    // Set the flag BEFORE sending, so the message handler is ready
    isBroadeningRef.current = true;

    // 1. Cancel the current queue entry
    sendRaw({ type: "cancel_queue" });

    // 2. Immediately re-join with the new interest — same tick, same socket
    queueStateRef.current = { inQueue: true, interest: newInterest };
    sendRaw({
      type:     "join_queue",
      nickname: nickname.trim(),
      interest: newInterest,
      userId:   currentUser.uid,
    });

    // queueStatus stays "queued" throughout — no setter call here
  }, [currentUser, sendRaw]);

  const sendMessage = useCallback((text) => {
    if (!roomIdRef.current) return;
    if (!text.trim()) return;

    // Generate a temp ID for optimistic UI — server will replace with stable UUID
    const tempId    = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const timestamp = Date.now();

    // Optimistic update — show message immediately
    setMessages(prev => [...prev, {
      id:        tempId,
      text:      text.trim(),
      sender:    "You",
      timestamp,
      isSelf:    true,
    }]);

    // Send to server with tempId so server can confirm back with stable ID
    sendRaw({ type: "message", text, roomId: roomIdRef.current, tempId });
  }, [sendRaw]);

  const sendTyping = useCallback((isTyping) => {
    sendRaw({ type: "typing", isTyping });
  }, [sendRaw]);

  /**
   * Send emoji reaction on a message to partner.
   * Also updates own message state optimistically.
   * messageId = msg.id, emoji = "❤️" | null (null = remove reaction)
   */
  const sendReaction = useCallback((messageId, emoji) => {
    if (!roomIdRef.current) return;
    // Optimistic update — update myReaction on this message locally
    setMessages(prev => prev.map(m =>
      m.id === messageId
        ? { ...m, myReaction: emoji }
        : m
    ));
    // Send to server to relay to partner
    sendRaw({ type: "reaction", messageId, emoji, roomId: roomIdRef.current });
  }, [sendRaw]);

  const leaveRoom = useCallback(() => {
    if (!roomIdRef.current) return;
    sendRaw({ type: "leave_room", roomId: roomIdRef.current });
    roomIdRef.current = null;
    setMatchData(null);
    setQueueStatus("idle");
    setMessages([]);
    setPartnerTyping(false);
  }, [sendRaw]);

  const resetError = useCallback(() => setError(null), []);

  return {
    status,
    queueStatus,
    matchData,
    messages,
    partnerTyping,
    error,
    joinQueue,
    cancelQueue,
    broadenQueue,
    sendMessage,
    sendTyping,
    sendReaction,
    leaveRoom,
    resetError,
  };
}